import React, { useEffect, useState } from "react";
import client from "../lib/api";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Search, Plus, Check, Lock, UserRound } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../i18n/LangContext";

// Required agent field: searchable list + inline "New agent" with WhatsApp de-dupe.
export const AgentPicker = ({ value, onChange, locked = false, agents: given }) => {
  const [agents, setAgents] = useState(given || []);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [nf, setNf] = useState({ name: "", agency: "", whatsapp: "", email: "" });
  const [busy, setBusy] = useState(false);
  const { t } = useLang();

  useEffect(() => { if (given) setAgents(given); else client.get("/agents").then((r) => setAgents(r.data)).catch(() => {}); }, [given]);
  const sel = agents.find((a) => a.id === value);
  const list = agents.filter((a) => `${a.name} ${a.agency} ${a.phone} ${a.whatsapp}`.toLowerCase().includes(q.toLowerCase())).slice(0, 8);

  const create = async () => {
    if (!nf.name.trim() || !nf.agency.trim()) { toast.error("Name and agency are required"); return; }
    if (!nf.whatsapp.trim()) { toast.error("WhatsApp number is required"); return; }
    setBusy(true);
    try {
      const r = await client.post("/agents", { name: nf.name, agency: nf.agency, whatsapp: nf.whatsapp, phone: nf.whatsapp, email: nf.email });
      if (r.data.existing) toast.info(t("This agent already exists — selected instead")); else { setAgents((xs) => [r.data, ...xs]); toast.success(t("Agent created")); }
      onChange(r.data.id, r.data); setCreating(false); setOpen(false);
    } catch (e) { toast.error("Couldn't create agent", { description: e?.response?.data?.detail }); } finally { setBusy(false); }
  };

  if (locked && sel) {
    return <div data-testid="agent-picker-locked" className="flex h-11 items-center gap-2 rounded-xl border border-white/10 bg-white/[0.03] px-3 text-sm text-white"><Lock className="h-4 w-4 text-muted-foreground" /> {sel.name} <span className="text-muted-foreground">· {sel.agency}</span></div>;
  }
  return (
    <div className="relative" data-testid="agent-picker">
      <button type="button" data-testid="agent-picker-toggle" onClick={() => setOpen((o) => !o)}
        className={`flex h-11 w-full items-center gap-2 rounded-xl border px-3 text-left text-sm ${sel ? "border-white/10 bg-white/[0.03] text-white" : "border-brand-red/40 bg-brand-red/[0.04] text-muted-foreground"}`}>
        <UserRound className="h-4 w-4 text-muted-foreground" />
        {sel ? <>{sel.name} <span className="text-muted-foreground">· {sel.agency}</span></> : t("Pick the agent who sent this listing *")}
      </button>
      {open && (
        <div className="absolute z-30 mt-1 w-full rounded-xl border border-white/10 bg-[#0b0f13] p-2 shadow-2xl">
          {!creating ? (
            <>
              <div className="relative"><Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" /><Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("Search agents by name, agency or phone…")} data-testid="agent-search" className="h-9 bg-input/60 pl-8 border-white/10" /></div>
              <div className="mt-1 max-h-56 overflow-y-auto">
                {list.map((a) => (
                  <button type="button" key={a.id} data-testid={`agent-option-${a.id}`} onClick={() => { onChange(a.id, a); setOpen(false); }}
                    className="flex w-full items-center justify-between rounded-lg px-2 py-2 text-left text-sm text-white hover:bg-white/5">
                    <span>{a.name} <span className="text-muted-foreground">· {a.agency}</span></span>
                    <span className="text-xs text-muted-foreground">{a.whatsapp || a.phone}</span>
                    {a.id === value && <Check className="h-4 w-4 text-brand-green" />}
                  </button>
                ))}
                {!list.length && <p className="px-2 py-3 text-xs text-muted-foreground">{t("No agents found")}</p>}
              </div>
              <button type="button" data-testid="agent-new-btn" onClick={() => { setCreating(true); setNf((f) => ({ ...f, name: q })); }} className="mt-1 flex w-full items-center gap-2 rounded-lg border border-dashed border-white/15 px-2 py-2 text-sm text-brand-green hover:bg-white/5"><Plus className="h-4 w-4" /> {t("New agent")}</button>
            </>
          ) : (
            <div className="space-y-2" data-testid="agent-new-form">
              <Input value={nf.name} onChange={(e) => setNf({ ...nf, name: e.target.value })} placeholder={`${t("Full name")} *`} data-testid="new-agent-name" className="h-10 bg-input/60 border-white/10" />
              <Input value={nf.agency} onChange={(e) => setNf({ ...nf, agency: e.target.value })} placeholder={`${t("Agency")} *`} data-testid="new-agent-agency" className="h-10 bg-input/60 border-white/10" />
              <Input value={nf.whatsapp} onChange={(e) => setNf({ ...nf, whatsapp: e.target.value })} placeholder={`${t("WhatsApp number")} * 0612345678`} inputMode="tel" data-testid="new-agent-whatsapp" className="h-10 bg-input/60 border-white/10" />
              <Input value={nf.email} onChange={(e) => setNf({ ...nf, email: e.target.value })} placeholder={t("Email (optional)")} type="email" data-testid="new-agent-email" className="h-10 bg-input/60 border-white/10" />
              <div className="flex gap-2">
                <Button type="button" onClick={create} disabled={busy} data-testid="new-agent-save" className="h-9 flex-1 bg-brand-green text-white hover:bg-brand-green/90">{t("Create agent")}</Button>
                <Button type="button" variant="outline" onClick={() => setCreating(false)} className="h-9 border-white/12 text-white">{t("Cancel")}</Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
